import { Injectable } from "@angular/core";
import {
  CldrIntlService,
  DateFormatOptions,
  IntlService,
  NumberFormatOptions
} from "@progress/kendo-angular-intl";
import { I18NextLoadResult, I18NextService } from "angular-i18next";
import {
  InitOptions,
  LanguageDetectorAsyncModule,
  LoggerModule,
  Services,
  TOptions
} from "i18next";
import XHR from "i18next-http-backend";
import localforage from "localforage";
import { combineLatest, from, Observable, of } from "rxjs";
import { AbstractUserService } from "src/app/interfaces";
import { LogClass, Logger } from "src/app/logger";
import { UserFacade } from "src/app/user/services/user.facade";

@Injectable({
  providedIn: "root"
})
@LogClass({ source: "I18nService" })
export class I18nService {
  private logger: Logger;
  private i18nextOptions: InitOptions;

  constructor(
    private translate: I18NextService,
    private intlService: IntlService,
    private userFacade: UserFacade,
    private userService: AbstractUserService
  ) {}

  public init(i18nextOptions: InitOptions): Promise<I18NextLoadResult> {
    this.i18nextOptions = i18nextOptions;
    const logger = new CircI18nLogger();
    const languageDetector = new CircI18nLangDetect(
      this.userFacade,
      this.userService,
      this.translate
    );
    const promise: Promise<I18NextLoadResult> = this.translate
      .use(XHR)
      .use(logger)
      .use(languageDetector)
      .init(i18nextOptions);
    promise.then(() => {
      this.use(this.translate.language);
    });
    return promise;
  }

  public formatDate(value: Date, format?: string | DateFormatOptions): string {
    return this.intlService.formatDate(value, format);
  }

  public formatNumber(
    value: number,
    format?: string | NumberFormatOptions
  ): string {
    return this.intlService.formatNumber(value, format);
  }

  /**
   * Gets the translated value of a key (or an array of keys)
   * @returns the translated key, or an object of translated keys
   */
  public get(
    key: string | Array<string>,
    // eslint-disable-next-line @typescript-eslint/ban-types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<string | any> {
    return of(this.translate.t(key));
  }

  public getLocale(): string {
    return this.translate.language;
  }

  /**
   * Switches the available languages.
   */
  public useNext(): void {
    const allowed = this.i18nextOptions.supportedLngs || [];
    let idx = allowed.indexOf(this.translate.language) + 1;
    if (idx >= allowed.length) {
      idx = 0;
    }
    const next = allowed[idx];
    this.logger.info(
      `Switching language from ${this.translate.language} to ${next}`
    );

    this.use(next);
  }

  public use(locale: string): void {
    // To dynamically change the CldrIntlService locale, set its localeId property.
    // The service notifies each subscribed component that changes in the service were made and
    // requires the component to update its output accordingly.
    (this.intlService as CldrIntlService).localeId = locale;
    // ngx-translate use
    this.translate.changeLanguage(locale);
  }
}

@LogClass({ source: "i18next" })
class CircI18nLogger implements LoggerModule {
  public type: "logger" = "logger";

  private logger: Logger;

  public log(...[msg, args]: unknown[]) {
    this.logger.debug(msg as string, args);
  }

  public warn(...[msg, args]: unknown[]) {
    this.logger.warn(msg as string, args);
  }

  public error(...[msg, args]: unknown[]) {
    this.logger.error(msg as string, args);
  }
}

@LogClass({ source: "i18nLanguageDetector" })
class CircI18nLangDetect implements LanguageDetectorAsyncModule {
  public type: "languageDetector" = "languageDetector";
  public async: true = true;

  private logger: Logger;
  private i18nextOptions: InitOptions;
  private cache: LocalForage;

  constructor(
    private userFacade: UserFacade,
    private userService: AbstractUserService,
    private translate: I18NextService
  ) {
    this.cache = localforage.createInstance({ name: "i18n" });
  }

  public init(
    _services: Services,
    _detectorOptions: Record<string, unknown>,
    i18nextOptions: InitOptions
  ) {
    this.i18nextOptions = i18nextOptions;
  }

  /** Must call callback passing detected language */
  public detect(callback: (lng: string) => void) {
    const getUser = this.userFacade
      .getUser()
      .pipe(this.logger.observableOperator("detect", "debug"));
    const getCache = this.getCachedLang();

    combineLatest([getUser, getCache]).subscribe(([u, cache]) => {
      const userLangs: string[] = [];

      // order: cache, preferred from DB, user profile values.
      if (cache) {
        userLangs.push(cache);
      }

      if (u.preferredLanguage) {
        userLangs.push(u.preferredLanguage);
      }

      if (u.languages) {
        u.languages.split(",").forEach((x) => {
          userLangs.push(x.trim());
        });
      }

      this.logger.debug("user languages: " + userLangs.join(";"));
      const validLangs = (this.i18nextOptions.supportedLngs || []).map((x) =>
        x.toLowerCase()
      );
      this.logger.debug("valid languages: " + validLangs.join(";"));
      for (const l of userLangs) {
        const lowerLangParts = l.toLowerCase().split("-"); // ["de", "de", "nds"]
        while (lowerLangParts.length > 0) {
          const test = lowerLangParts.join("-");
          this.logger.debug(`testing whether ${test} is a valid language...`);

          const idx = validLangs.indexOf(test);
          if (idx > -1) {
            const match = this.i18nextOptions.supportedLngs[idx];
            this.logger.info(
              `found user language: ${match} (Current language is: ${this.translate.language})`
            );
            if (this.translate.language != match) {
              callback(match);
            }
            return;
          }

          lowerLangParts.splice(lowerLangParts.length - 1, 1);
        }
      }

      // no match at all: calculate fallback.
      let fallback: string = this.i18nextOptions.fallbackLng as string;
      if (
        Array.isArray(this.i18nextOptions.fallbackLng) &&
        this.i18nextOptions.fallbackLng.length > 0
      ) {
        fallback = (this.i18nextOptions.fallbackLng as string[])[0];
      }
      if (!fallback) {
        fallback = this.i18nextOptions.supportedLngs[0];
      }
      this.logger.info("setting fallback language: " + fallback);
      callback(fallback);
    });
  }

  public cacheUserLanguage(lng: string): void {
    combineLatest([
      this.userService.setPreferredLanguage(lng),
      this.setCachedLang(lng)
    ]).subscribe(() => {
      this.logger.debug("saved preferred language: " + lng);
    });
  }

  private getCachedLang(): Observable<string> {
    return from(this.cache.getItem<string>("preferredLanguage"));
  }

  private setCachedLang(lang: string): Observable<string> {
    return from(this.cache.setItem<string>("preferredLanguage", lang));
  }
}
